import { getRouteApi } from "@tanstack/react-router";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { NOTARY_ACTS, getNotaryAct, type NotaryAct } from "@/data/notary-acts";
import { getLocation, locations, type Location } from "@/data/locations";
import { getDistrictContext } from "@/data/district-context";
import { SERVICES } from "@/data/services";
import { buildCombo } from "@/data/combo-content";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { FaqAccordion, ContactCTA } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { RelatedComparisons } from "@/components/RelatedComparisons";
import { RelatedServicesInLocation } from "@/components/RelatedServicesInLocation";
import { EntityPanel } from "@/components/EntityPanel";
import NotaryActDeepContent from "@/components/NotaryActDeepContent";
import {
  ScrollText,
  MapPin,
  FileCheck2,
  ShieldCheck,
  Clock,
  MessageCircle,
  Phone,
  BadgeCheck,
  ArrowRight,
  Building2,
  Landmark,
  Train,
} from "lucide-react";
import { isTopNotaryAct } from "@/routes/notary.$act.in.$location";

const routeApi = getRouteApi("/notary/$act/in/$location");

export function NotaryActLocationPage() {
  const { actSlug, locSlug } = routeApi.useLoaderData() as { actSlug: string; locSlug: string };
  const n = getNotaryAct(actSlug)!;
  const loc = getLocation(locSlug)!;
  const ctx = getDistrictContext(locSlug);
  const combo = buildCombo(SERVICES["notary-public"], loc);

  const related = NOTARY_ACTS.filter((x) => x.slug !== n.slug && x.category === n.category).slice(
    0,
    6,
  );
  const otherTopActs = NOTARY_ACTS.filter((x) => isTopNotaryAct(x.slug) && x.slug !== n.slug).slice(
    0,
    6,
  );

  const nearby = locations
    .filter((l) => l.slug !== loc.slug && l.region === loc.region)
    .slice(0, 8);

  const faqs = [
    {
      q: `${n.nameTh} ที่${loc.nameTh} ราคาเริ่มเท่าไหร่?`,
      a: `เริ่ม ฿${n.priceFromTh.toLocaleString()} เสร็จ ${n.turnaround} โดยทนาย Notarial Services Attorney ขึ้นทะเบียนสภาทนายความฯ — รวมตราประทับ Notary + Notarial Certificate ใช้ยื่นสถานทูต/MFA ได้ทันที สำหรับลูกค้าใน${loc.nameTh} มีบริการรับ-ส่งและทนายเดินทางไปหา`,
    },
    {
      q: `ใช้ ${n.nameEn} กับประเทศใดได้บ้าง?`,
      a: `ใช้ได้ทั่วโลกหลังผ่านขั้น Legalization ที่เหมาะสม — ประเทศสมาชิก Hague (124 ประเทศ) ใช้ Apostille ของกระทรวงการต่างประเทศไทย (มีผล 13 ธ.ค. 2566) ส่วนนอก Hague ใช้รับรองสถานทูตปลายทาง NYC ดำเนินการต่อให้ครบวงจรไม่ต้องวิ่งหลายที่`,
    },
    {
      q: `${n.nameTh} เซ็นทางออนไลน์ได้ไหม?`,
      a: `ไม่ได้ — กฎหมายไทยกำหนดให้เซ็นต่อหน้าทนาย Notarial Services Attorney เท่านั้น (ระเบียบสภาทนายความว่าด้วยการขึ้นทะเบียนทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร พ.ศ.2551) ทางเลือก: ทนายเดินทางไปหาที่${loc.nameTh} +฿1,500 หรือนัดเข้าสำนักงานลาดพร้าว 95`,
    },
    {
      q: `ลูกค้า${loc.nameTh}ส่งเอกสารยังไง?`,
      a: `3 ทาง: (1) สแกน/ถ่ายรูปส่ง LINE @nycli ทีมประเมิน 15-30 นาที (2) Walk-in สำนักงาน (3) ทนายเดินทางไปหาในพื้นที่${loc.nameTh} — เซ็นจริงต้องต่อหน้าทนายเท่านั้น`,
    },
    {
      q: `${n.nameTh} ต้องเตรียมเอกสารอะไร?`,
      a: `${n.required.join(" · ")} — เอกสารตัวจริงทุกฉบับ + ID/Passport ของผู้เซ็นทุกคน หากเป็นภาษาต่างประเทศแนะนำให้แปลและตรวจก่อนเซ็นเพื่อหลีกเลี่ยงตีกลับ`,
    },
    {
      q: `${n.nameTh} เสร็จกี่วัน?`,
      a: `Standard ${n.turnaround} | Same-day +฿1,000 (รับงานก่อน 11:00) | ถ้าต่อ Apostille/MFA เพิ่มอีก 1-3 วันทำการ | ถ้าต่อสถานทูต 2-5 วันแล้วแต่ประเทศปลายทาง`,
    },
    {
      q: `รับ-ส่งเอกสารถึง${loc.nameTh}ได้ไหม?`,
      a: `ได้ — EMS / Grab Express / Lalamove / Messenger NYC ครอบคลุมทั้งกรุงเทพฯและทุกจังหวัด ค่าบริการเริ่ม ฿80 (ในกทม.) หรือ EMS ตามอัตรากรมไปรษณีย์`,
    },
    {
      q: `${n.nameEn} กับ ${n.nameTh} เหมือนกันไหม?`,
      a: `เป็นบริการเดียวกัน — ${n.nameEn} เป็นชื่อภาษาอังกฤษที่ใช้สากล ส่วน ${n.nameTh} คือชื่อภาษาไทยตามระเบียบสภาทนายความ ทั้งสองชื่ออ้างถึง Notarial Act ประเภทเดียวกัน Notarial Certificate ที่ออกจะระบุทั้ง 2 ภาษา`,
    },
    {
      q: `เคยถูกตีกลับจากสถานทูตไหม?`,
      a: `เรามี Pass-rate 98.6% ในรอบ 24 เดือน — กรณีที่ตีกลับมักเป็นเอกสารต้นทางไม่ครบ (เช่น สำเนาทะเบียนบ้านไม่ใหม่) เรามีระบบ Pre-check แจ้งล่วงหน้าก่อนเซ็นเพื่อหลีกเลี่ยงปัญหานี้`,
    },
    {
      q: `เก็บใบเสร็จ/ใบกำกับภาษีได้ไหม?`,
      a: `ได้ — ใบกำกับภาษีเต็มรูปภายใต้ NYC Legal & Notary Service Co., Ltd. (DBD 0435567000061) ใช้หักลดหย่อนภาษีบุคคล/นิติบุคคลได้ครบถ้วน`,
    },
  ];

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div
          className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]"
          aria-hidden
        />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav
            aria-label="Breadcrumb"
            className="mb-5 flex flex-wrap gap-x-1 text-[12px] text-white/80"
          >
            <Link
              to="/"
              className="min-h-[36px] inline-flex items-center px-1.5 py-1.5 hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 rounded"
            >
              หน้าแรก
            </Link>
            <span className="px-1 py-1.5 text-white/30">/</span>
            <Link
              to="/notary"
              className="min-h-[36px] inline-flex items-center px-1.5 py-1.5 hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 rounded"
            >
              Notary
            </Link>
            <span className="px-1 py-1.5 text-white/30">/</span>
            <Link
              to="/notary/$act"
              params={{ act: n.slug }}
              className="min-h-[36px] inline-flex items-center px-1.5 py-1.5 hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 rounded"
            >
              {n.nameTh}
            </Link>
            <span className="px-1 py-1.5 text-white/30">/</span>
            <span className="min-h-[36px] inline-flex items-center px-1.5 py-1.5 text-white/85">
              {loc.nameTh}
            </span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ScrollText className="h-3.5 w-3.5" /> Notary Public · {n.nameEn} · {loc.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            {n.nameTh} ที่{loc.nameTh}
          </h1>
          <p className="mt-2 text-base text-white/80">
            {n.nameEn} · {loc.nameEn}
          </p>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{combo.intro}</p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "ราคาเริ่ม", v: `฿${n.priceFromTh.toLocaleString()}` },
              { k: "เสร็จภายใน", v: n.turnaround },
              { k: "พื้นที่", v: loc.nameTh },
              { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div
                key={s.k}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur"
              >
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a
              href="https://line.me/ti/p/@nycli"
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
            >
              <MessageCircle className="h-4 w-4" /> ปรึกษา LINE — ตอบ 15 นาที
            </a>
            <a
              href="tel:0832494999"
              className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
            >
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          {combo.aeoAnswer} — สำหรับบริการ {n.nameTh} ({n.nameEn}) เริ่ม ฿
          {n.priceFromTh.toLocaleString()} เสร็จ {n.turnaround}
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <MapPin className="inline h-6 w-6 text-amber-600" /> บริการ {n.nameTh} ใน{loc.nameTh}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">
            {combo.localContext}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">
            {combo.localExpertise}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">
            {combo.deliveryLine}
          </p>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            ใช้สำหรับ — {n.nameTh}
          </h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {n.useCases.map((u) => (
              <li
                key={u}
                className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800"
              >
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {u}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm leading-relaxed text-slate-700 sm:text-base">
            สำหรับลูกค้าในพื้นที่{loc.nameTh} เคสที่พบบ่อยที่สุดคือใช้ {n.nameTh}{" "}
            ประกอบเอกสารยื่นสถานทูต/หน่วยงานต่างประเทศ ทั้งกลุ่มนักศึกษา (แปลผลการเรียน + Notarized
            Translation Cert), กลุ่มทำงานต่างประเทศ (Affidavit of Single Status, FBI Background
            Check, Police Clearance), กลุ่มธุรกิจ (POA, Corporate Resolution, Certificate of
            Incumbency สำหรับเปิดบัญชี/ตั้งบริษัทต่างประเทศ) และกลุ่มครอบครัวข้ามชาติ (จดทะเบียนสมรส
            K-1/Spouse Visa, รับมรดกต่างประเทศ, Travel Consent สำหรับเด็ก)
          </p>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            เอกสารที่ต้องเตรียม
          </h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {n.required.map((r) => (
              <li
                key={r}
                className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white p-3.5 text-sm text-slate-800"
              >
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {r}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm leading-relaxed text-slate-700 sm:text-base">
            ลูกค้าใน{loc.nameTh}สามารถส่งสำเนาเอกสารทั้งหมดผ่าน LINE @nycliให้ทีม Pre-check ก่อน —
            เราจะตรวจความครบถ้วนและความถูกต้องของชื่อ-สกุล วันที่ ลายเซ็นพยาน
            และความสอดคล้องระหว่างเอกสาร (Cross-reference)
            แล้วแจ้งรายการที่ขาดหรือต้องแก้ก่อนนัดวันเซ็น เพื่อให้การพบทนายครั้งเดียวจบ —
            ลดต้นทุนเดินทางและเวลาของลูกค้า
          </p>
        </section>

        <section className="mt-12 rounded-3xl border border-slate-200 bg-slate-50/50 p-6 sm:p-8">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            ขั้นตอนทำงานละเอียด — {loc.nameTh}
          </h2>
          <ol className="mt-5 space-y-4">
            {combo.timeline.map((t, i) => (
              <li
                key={i}
                className="flex gap-4 rounded-2xl border border-white bg-white p-4 shadow-sm"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/20 font-display text-sm font-extrabold text-amber-700">
                  {i + 1}
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">
                    {t.day}
                  </div>
                  <h3 className="font-display text-base font-extrabold text-navy-deep">
                    {t.title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-700">{t.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            ทำไมเลือก NYC — {loc.nameTh}
          </h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {combo.whyUs.map((w, i) => (
              <li
                key={i}
                className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white p-3.5 text-sm text-slate-800"
              >
                <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {w}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            กระบวนการเชิงลึก
          </h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {combo.processDeep.map((p, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4">
                <h3 className="font-display text-base font-extrabold text-navy-deep">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            ข้อผิดพลาดที่พบบ่อยและวิธีหลีกเลี่ยง
          </h2>
          <ul className="mt-5 space-y-3">
            {combo.commonMistakes.map((m, i) => (
              <li key={i} className="rounded-2xl border border-rose-200 bg-rose-50/40 p-4">
                <div className="text-[11px] font-bold uppercase tracking-widest text-rose-700">
                  ผิดพลาด
                </div>
                <p className="mt-1 text-sm font-semibold text-slate-900">{m.mistake}</p>
                <div className="mt-2 text-[11px] font-bold uppercase tracking-widest text-emerald-700">
                  วิธีป้องกัน
                </div>
                <p className="mt-1 text-sm text-slate-700">{m.prevention}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12 rounded-2xl border border-navy-deep/10 bg-navy-deep/5 p-5">
          <h2 className="font-display text-xl font-extrabold text-navy-deep sm:text-2xl">
            <Landmark className="inline h-5 w-5 text-amber-600" /> กรอบกฎหมายที่อ้างอิง
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">{combo.legalRefs}</p>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
            ราคา · {loc.nameTh}
          </div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            {n.nameTh} เริ่ม ฿{n.priceFromTh.toLocaleString()}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-700 sm:text-base">
            {combo.costBreakdown}
          </p>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Standard:{" "}
              {n.turnaround}
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Same-day +฿1,000
              (ก่อน 11:00)
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ทนายเดินทางไปหา
              +฿1,500 ในกทม. / +฿2,500-5,000 ต่างจังหวัด
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> +Apostille MFA
              ฿2,500 (รวมค่าธรรมเนียม)
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> +Embassy
              Legalization ฿3,000-8,000 (แล้วแต่ประเทศ)
            </li>
            <li className="flex items-start gap-2 text-sm text-slate-700">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{" "}
              ออกใบกำกับภาษีเต็มรูป (VAT 7%)
            </li>
          </ul>
          <div className="mt-6">
            <ContactCTA />
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            เทียบทางเลือกในตลาด
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">
            {combo.comparison}
          </p>
        </section>

        <section className="mt-12 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
            รีวิวจากลูกค้า
          </div>
          <blockquote className="mt-3 font-display text-lg leading-relaxed text-navy-deep sm:text-xl">
            “{combo.testimonial.quote}”
          </blockquote>
          <div className="mt-3 text-sm font-semibold text-slate-700">
            — {combo.testimonial.author} · {combo.testimonial.role}
          </div>
        </section>

        <section className="mt-12 rounded-2xl border border-amber-200 bg-amber-50/40 p-5">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
            หมายเหตุประจำฤดู / Timeline tip
          </div>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">{combo.seasonalNote}</p>
        </section>

        {(ctx.transit.length > 0 || ctx.malls.length > 0 || ctx.landmarks.length > 0) && (
          <section className="mt-12">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
              จุดให้บริการใกล้เคียง — {loc.nameTh}
            </h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              {ctx.transit.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-amber-600">
                    <Train className="h-3.5 w-3.5" /> ขนส่ง
                  </div>
                  <ul className="mt-2 space-y-1 text-sm text-slate-700">
                    {ctx.transit.slice(0, 6).map((t) => (
                      <li key={t}>• {t}</li>
                    ))}
                  </ul>
                </div>
              )}
              {ctx.malls.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-amber-600">
                    <Building2 className="h-3.5 w-3.5" /> ห้าง / จุดนัดพบ
                  </div>
                  <ul className="mt-2 space-y-1 text-sm text-slate-700">
                    {ctx.malls.slice(0, 6).map((m) => (
                      <li key={m}>• {m}</li>
                    ))}
                  </ul>
                </div>
              )}
              {ctx.landmarks.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-amber-600">
                    <Landmark className="h-3.5 w-3.5" /> แลนด์มาร์ก
                  </div>
                  <ul className="mt-2 space-y-1 text-sm text-slate-700">
                    {ctx.landmarks.slice(0, 6).map((l) => (
                      <li key={l}>• {l}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-700">{combo.neighborLine}</p>
          </section>
        )}

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            คำถามที่พบบ่อย — {n.nameTh} ที่{loc.nameTh}
          </h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion items={faqs} />
          </div>
        </section>

        {otherTopActs.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
              บริการ Notary อื่น ที่{loc.nameTh}
            </h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherTopActs.map((a) => (
                <Link
                  key={a.slug}
                  to="/notary/$act/in/$location"
                  params={{ act: a.slug, location: loc.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">
                    {a.nameEn}
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                      {a.nameTh} · {loc.nameTh}
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-amber-600" />
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">
                    เริ่ม ฿{a.priceFromTh.toLocaleString()} · {a.turnaround}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {nearby.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
              {n.nameTh} · พื้นที่ใกล้เคียง
            </h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {nearby.map((l) => (
                <Link
                  key={l.slug}
                  to="/notary/$act/in/$location"
                  params={{ act: n.slug, location: l.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">
                    {l.region}
                  </div>
                  <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                    {n.nameTh} · {l.nameTh}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
              บริการที่เกี่ยวข้อง
            </h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to="/notary/$act"
                  params={{ act: r.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                >
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                    {r.nameTh}
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    เริ่ม ฿{r.priceFromTh.toLocaleString()} · {r.turnaround}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            บริการอื่นใน{loc.nameTh}
          </h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Link
              to="/services/$service/$location"
              params={{ service: "notary-public", location: loc.slug }}
              className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
            >
              <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                Notary Public ครบวงจร · {loc.nameTh}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">
                หน้าฮับบริการ Notary ทั้งหมดในพื้นที่
              </div>
            </Link>
            <Link
              to="/services/$service/$location"
              params={{ service: "translation", location: loc.slug }}
              className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
            >
              <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                แปลและรับรองเอกสาร · {loc.nameTh}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">50+ ภาษา · ใช้คู่กับ Notary</div>
            </Link>
            <Link
              to="/services/$service/$location"
              params={{ service: "mfa-legalization", location: loc.slug }}
              className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
            >
              <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                รับรองกงสุล MFA · {loc.nameTh}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">นิติกรณ์กระทรวงการต่างประเทศ</div>
            </Link>
          </div>
        </section>
      </div>

      <EntityPanel slug={loc.slug} locationName={loc.nameTh} locale="th" variant="light" />
      <RelatedServicesInLocation currentService="notary" locationSlug={locSlug} locale="th" />
      <RelatedComparisons category="notary" locale="th" />
      <NotaryActDeepContent act={n} lang="th" />
      <TrustLayer variant="compact" />
    </main>
  );
}
